import { Box, MenuItem, Select, Theme, Typography } from "@mui/material";
import ResultsViewToggle from "../FusionCharts/ResultsViewToggle";
import { useEffect, useRef, useState } from "react";
import TableChartIcon from "@mui/icons-material/TableChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import { ApiBaseUrl } from "../../config";
import {
  Query,
  ResultRecord,
  SimpleTableRow,
  TableHeader,
} from "../../interfaces";
import { fetchData } from "../../common/services/ApiClient";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import FibFinancialsChart from "../FusionCharts/FIBFinancialsChart";
import ApiDataDownload from "../ApiDataDownload";
import { fileNameFormat } from "../../common/services/Utilities";
import { useBreakpoint } from "../../hooks/useBreakpoint";

type Report =
  | "assets-qtly"
  | "assets-compare"
  | "netinc-annual"
  | "netinc-compare"
  | "netint-qtly"
  | "netint-compare";

interface DropDownOptions {
  label: string;
  value: Report;
}

interface Data {
  RISDATE: string;
  ASSET?: number;
  NETINC?: number;
  NIM?: number;
  id: string;
}

const assetCompareChartConfig = {
  showValues: "1",
  valueFontColor: "#ffffff",
  placeValuesInside: "1",
  valueFontSize: "18",
  maxColWidth: "200",
  divLineGapLen: "4",
  valueFontBold: "1",
  plotSpacePercent: "20",
  showToolTip: "0",
  paletteColors: "#A48923,#00908C",
};

const netincCompareConfig = {
  ...assetCompareChartConfig,
  xAxisName: "API  Variable Queried: NETINC",
  showToolTip: "0",
};

const netincChartConfig = {
  paletteColors: "#A48923",
  showValues: "1",
  placeValuesInside: "1",
  valueFontColor: "#ffffff",
  valueFontBold: "1",
  xAxisName: "API  Variable Queried: NETINC",
  showToolTip: "0",
  plotSpacePercent: "40",
};

const netintQtlyChartConfig = {
  xAxisName: "API  Variable Queried: NIM",
};

const netintCompareChartConfig = {
  ...assetCompareChartConfig,
  xAxisName: "API  Variable Queried: NIM",
  showToolTip: "0",
};

const getTableHeaders = (apiVar: string) => {
  const infoHeaders: TableHeader[] = [
    {
      title: `API Variable Queried: ${apiVar}`,
      colSpan: 3,
      styles: {
        fontSize: 14,
        textAlign: "left",
        backgroundColor: "#4b76b7",
        color: "white",
      },
    },
    {
      title: "Values are in Thousands (USD)",
      colSpan: 2,
      styles: {
        fontSize: 14,
        textAlign: "right",
        backgroundColor: "#4b76b7",
        color: "white",
      },
    },
  ];

  const assetQtlyTableHeaders: TableHeader[][] = [
    [
      {
        title: "Year",
        apiField: "yr",
      },
      {
        title: "Q1",
        apiField: "q1",
      },
      {
        title: "Q2",
        apiField: "q2",
      },
      {
        title: "Q3",
        apiField: "q3",
      },
      {
        title: "Q4",
        apiField: "q4",
      },
    ].map((c) => ({
      ...c,
      colSpan: 1,
      styles: { fontSize: 16, fontWeight: "bold", width: 100 },
    })),
    infoHeaders,
  ];

  return assetQtlyTableHeaders;
};

const getCompareHeaders = (
  start: string,
  end: string,
  apiVar: string,
): TableHeader[][] => {
  const startDate = `${start.slice(0, 2)}/${start.slice(2, 4)}/${start.slice(4)}`;
  const endDate = `${end.slice(0, 2)}/${end.slice(2, 4)}/${end.slice(4)}`;
  return [
    [
      {
        title: startDate,
        apiField: "start",
        colSpan: 1,
      },
      {
        title: endDate,
        apiField: "end",
        colSpan: 1,
      },
    ].map((c) => ({
      ...c,
      styles: {
        fontSize: 16,
        fontWeight: "bold",
        width: 100,
        textAlign: "right",
      },
    })),
    [
      {
        title: `API Variable Queried: ${apiVar}`,
        colSpan: 1,
        styles: {
          fontSize: 14,
          textAlign: "left",
          backgroundColor: "#4b76b7",
          color: "white",
        },
      },
      {
        title: "Values are in Thousands (USD)",
        colSpan: 1,
        styles: {
          fontSize: 14,
          textAlign: "right",
          backgroundColor: "#4b76b7",
          color: "white",
        },
      },
    ],
  ];
};

const getNetincHeaders = (years: string[]): TableHeader[][] => {
  return [
    [
      ...years.map((yr) => ({
        title: yr,
        apiField: yr,
        colSpan: 1,
      })),
    ].map((c) => ({
      ...c,
      styles: {
        fontSize: 16,
        fontWeight: "bold",
        width: 100,
        ...(years.length === 2 && {
          textAlign: "right",
        }),
      },
    })),
    [
      {
        title: "API Variable Queried: NETINC",
        colSpan: years.length / 2,
        styles: {
          fontSize: 14,
          textAlign: "left",
          backgroundColor: "#4b76b7",
          color: "white",
        },
      },
      {
        title: "Values are in Thousands (USD)",
        colSpan: years.length / 2,
        styles: {
          fontSize: 14,
          textAlign: "right",
          backgroundColor: "#4b76b7",
          color: "white",
        },
      },
    ],
  ];
};

const quarterKeyMap: Record<string, string> = {
  "0331": "q1",
  "0630": "q2",
  "0930": "q3",
  "1231": "q4",
};

const dropDownOptions: DropDownOptions[] = [
  { label: "Assets - Quarterly Five Years", value: "assets-qtly" },
  { label: "Assets - Compare Quarters", value: "assets-compare" },
  { label: "Net Income - Annual Past Ten Years", value: "netinc-annual" },
  { label: "Net Income - Compare Years", value: "netinc-compare" },
  {
    label: "Net Interest Income - Quarterly Five Years",
    value: "netint-qtly",
  },
  {
    label: "Net Interest Income - Quarter Comparisons",
    value: "netint-compare",
  },
];

const titles: Record<Report, string> = {
  "assets-qtly": "Quarterly Assets - Past Five Years",
  "assets-compare": "Quarterly Assets - Compare Quarters",
  "netinc-annual": "Annual Net Income Attributable to Bank - Past Ten Years",
  "netinc-compare": "Annual Net Income Attributable to Bank - Compare Years",
  "netint-qtly": "Quarterly Net Interest Income - Past Five Years",
  "netint-compare": "Quarterly Net Interest Income - Quarter Comparisons",
};

const convertRisdateToDate = (risdate: string) => {
  const y = risdate.slice(0, 4);
  const m = risdate.slice(4, 6);
  const d = risdate.slice(6);
  return `${m}/${d}/${y}`;
};

function transformToChartData(records: Data[], report: Report): any {
  if (report === "assets-compare" || report === "netint-compare") {
    const categories = [
      {
        category: [{ label: "" }],
      },
    ];
    const dataset = records.map((record) => ({
      seriesName: convertRisdateToDate(record.RISDATE),
      data: [
        {
          value: String(
            report === "assets-compare" ? record.ASSET : record.NIM,
          ),
        },
      ],
    }));
    return { categories, dataset };
  } else if (report === "netinc-annual") {
    const categories = null;
    const dataset = records.map((rec) => ({
      label: rec.RISDATE.slice(0, 4),
      value: rec.NETINC,
    }));
    return { categories, dataset };
  } else if (report === "netinc-compare") {
    const categories = [
      {
        category: [{ label: "" }],
      },
    ];
    const dataset = records.map((record) => ({
      seriesName: record.RISDATE.slice(0, 4),
      data: [{ value: String(record.NETINC) }],
    }));
    return { categories, dataset };
  }
  const QUARTERS: Record<string, string> = {
    "03": "Q1",
    "06": "Q2",
    "09": "Q3",
    "12": "Q4",
  };

  const grouped: Record<string, Record<string, number>> = {};

  for (const record of records) {
    const year = record.RISDATE.slice(0, 4);
    const month = record.RISDATE.slice(4, 6);
    const quarter = QUARTERS[month];

    if (!quarter) continue;
    if (!grouped[year]) grouped[year] = {};
    grouped[year][quarter] = record.ASSET
      ? record.ASSET
      : record.NIM
        ? record.NIM
        : 0;
  }

  const years = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  const categories = [
    {
      category: years.flatMap((year, index) => {
        const items = [];
        if (index > 0) {
          items.push({
            vLine: "1",
            color: "#d8d8d8",
            lineThickness: "1",
            label: " ",
            showLabelBorder: "0",
          });
        }
        items.push({ label: year });
        return items;
      }),
    },
  ];
  const dataset = ["Q1", "Q2", "Q3", "Q4"].map((q) => ({
    seriesName: q,
    data: years.map((year) => ({
      value: String(grouped[year]?.[q]),
    })),
  }));

  return { categories, dataset };
}

const formatDollar = (asset: number) =>
  asset.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const BankFindFinancialsDataViz = ({
  institution,
  appState,
}: {
  institution: ResultRecord;
  appState: Query;
}) => {
  const { smallScreen } = useBreakpoint();
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [dataView, setDataView] = useState<string>("table");
  const [report, setReport] = useState<Report>("assets-qtly");
  const [title, setTitle] = useState<string>(
    "Quarterly Assets - Past Five Years",
  );
  const [data, setData] = useState<Data[]>([]);
  const [tableData, setTableData] = useState<SimpleTableRow[]>([]);
  const [tableHeaders, setTableHeaders] = useState<TableHeader[][]>(
    getTableHeaders("ASSET"),
  );
  const [risdate, setRisdate] = useState("");
  const m = risdate.slice(4, 6);
  const d = risdate.slice(6);
  const risdateYr = risdate.slice(0, 4);
  const risdateQ = risdate.slice(4);
  let startQ = risdateQ;
  startQ =
    startQ === "1231"
      ? "0331"
      : startQ === "0331"
        ? "0630"
        : startQ === "0630"
          ? "0930"
          : "1231";
  const startYear = startQ === "0331" ? `${Number(risdateYr) + 1}` : risdateYr;
  const risdateRangeStart = `${parseInt(startYear) - 5}${startQ}`;
  const risdateRangeEnd = `${parseInt(startYear)}${m}${d}`;

  const [earliestRisdateYr, setEarliestRisdateYr] = useState<number>(1984);
  const [earliestRisdateQ, setEarliestRisdateQ] = useState<string>("");
  const [compareStartYr, setCompareStartYr] = useState("");
  const [compareStartQ, setCompareStartQ] = useState("");
  const [compareEndYr, setCompareEndYr] = useState("");
  const [compareEndQ, setCompareEndQ] = useState("");
  const [invalidDateRange, setInvalidDateRange] = useState<boolean>(false);
  const [netincCompareYrStart, setNetincCompareYrStart] = useState<number>(0);
  const [netincCompareYrEnd, setNetincCompareYrEnd] = useState<number>(1);

  const compareOptionsYr: any[] = [];

  for (let i = Number(risdateYr); i >= earliestRisdateYr; i--) {
    compareOptionsYr.push(String(i));
  }

  let compareOptionsStartQ = [
    { label: "Q1", value: "0331" },
    { label: "Q2", value: "0630" },
    { label: "Q3", value: "0930" },
    { label: "Q4", value: "1231" },
  ];
  let compareOptionsEndQ = [...compareOptionsStartQ];

  if (Number(compareStartYr) === earliestRisdateYr) {
    if (earliestRisdateQ === "0630") {
      compareOptionsStartQ.splice(0, 1);
    } else if (earliestRisdateQ === "0930") {
      compareOptionsStartQ.splice(0, 2);
    } else if (earliestRisdateQ === "1231") {
      compareOptionsStartQ.splice(0, 3);
    }
  } else if (compareStartYr === risdateYr) {
    if (risdateQ === "0331") {
      compareOptionsStartQ.splice(1, 3);
    } else if (risdateQ === "0630") {
      compareOptionsStartQ.splice(2, 2);
    } else if (risdateQ === "0930") {
      compareOptionsStartQ.splice(3, 1);
    }
  }
  if (Number(compareEndYr) === earliestRisdateYr) {
    if (earliestRisdateQ === "0630") {
      compareOptionsEndQ.splice(0, 1);
    } else if (earliestRisdateQ === "0930") {
      compareOptionsEndQ.splice(0, 2);
    } else if (earliestRisdateQ === "1231") {
      compareOptionsEndQ.splice(0, 3);
    }
  } else if (compareEndYr === risdateYr) {
    if (risdateQ === "0331") {
      compareOptionsEndQ.splice(1, 3);
    } else if (risdateQ === "0630") {
      compareOptionsEndQ.splice(2, 2);
    } else if (risdateQ === "0930") {
      compareOptionsEndQ.splice(3, 1);
    }
  }

  const chartInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (initialLoad) {
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT}&fields=RISDATE&limit=1&sort_by=RISDATE&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT}&fields=RISDATE&limit=1&sort_by=RISDATE&sort_order=DESC`,
          ),
        ]).then((res) => {
          setRisdate(res[1].data[0].RISDATE);
          setEarliestRisdateYr(Number(res[0].data[0].RISDATE.slice(0, 4)));
          setEarliestRisdateQ(res[0].data[0].RISDATE.slice(4));
        });
      })();
      setInitialLoad(false);
    }
  }, [initialLoad, institution.CERT]);

  useEffect(() => {
    if (!risdate || invalidDateRange) return;
    if (report === "assets-qtly" || report === "netint-qtly") {
      const apiVar = report === "assets-qtly" ? "ASSET" : "NIM";
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT} AND RISDATE:[${risdateRangeStart} TO ${risdateRangeEnd}]&fields=${apiVar},RISDATE&limit=20&sort_by=RISDATE&sort_order=ASC`,
        ).then((res) => {
          setData(res.data);
          const newtableData = Object.entries(
            (res.data as Data[]).reduce(
              (acc, { RISDATE, ASSET, NIM }: any) => {
                const year = RISDATE.slice(0, 4);
                const quarter = quarterKeyMap[RISDATE.slice(4)];
                if (!acc[year]) acc[year] = {};
                acc[year][quarter] = {
                  value: formatDollar(apiVar === "ASSET" ? ASSET : NIM),
                };
                return acc;
              },
              {} as Record<string, Record<string, { value: string | null }>>,
            ),
          ).map(([year, quarters], i) => ({
            yr: { value: year, styles: { fontWeight: "bold", fontSize: 16 } },
            ...Object.fromEntries(
              ["q1", "q2", "q3", "q4"].map((q) => [
                q,
                quarters[q]
                  ? {
                      ...quarters[q],
                      styles: { fontSize: 16 },
                    }
                  : {
                      value: "-",
                      styles: { fontSize: 16 },
                    },
              ]),
            ),
          }));
          setTableData(newtableData);
          setTableHeaders(getTableHeaders(apiVar));
          const mostRecent = String(res.data.at(-1).RISDATE);
          const yr = mostRecent.slice(0, 4);
          const q = mostRecent.slice(4);
          setNetincCompareYrStart(Number(yr) - 1);
          setNetincCompareYrEnd(Number(yr));
          setCompareEndYr(yr);
          setCompareEndQ(q);
          setCompareStartYr(q === "0331" ? String(Number(yr) - 1) : yr);
          setCompareStartQ(
            q === "1231"
              ? "0930"
              : q === "0930"
                ? "0630"
                : q === "0630"
                  ? "0331"
                  : "1231",
          );
        });
      })();
    } else if (report === "assets-compare" || report === "netint-compare") {
      const apiVar = report === "assets-compare" ? "ASSET" : "NIM";

      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT} AND RISDATE:(${compareStartYr}${compareStartQ} OR ${compareEndYr}${compareEndQ})&fields=${apiVar},RISDATE&limit=2&sort_by=RISDATE&sort_order=ASC`,
        ).then((res) => {
          let data = [...res.data];
          setData(data);
          setTableHeaders(
            getCompareHeaders(
              `${compareStartQ}${compareStartYr}`,
              `${compareEndQ}${compareEndYr}`,
              apiVar,
            ),
          );
          setTableData([
            {
              start: {
                value: data[0]?.ASSET
                  ? formatDollar(data[0].ASSET)
                  : data[0]?.NIM
                    ? formatDollar(data[0].NIM)
                    : "-",
                styles: { textAlign: "right", fontSize: 16 },
              },
              end: {
                value: data[1]?.ASSET
                  ? formatDollar(data[1].ASSET)
                  : data[1]?.NIM
                    ? formatDollar(data[1].NIM)
                    : "-",
                styles: { textAlign: "right", fontSize: 16 },
              },
            },
          ]);
        });
      })();
    } else if (report === "netinc-annual") {
      const risdateArray: string[] = [];
      for (
        let i = Number(risdate.slice(0, 4));
        i > Number(risdate.slice(0, 4)) - 10;
        i--
      ) {
        if (i === Number(risdateYr)) {
          risdateArray.push(`${i}${risdateQ}`);
        } else {
          risdateArray.push(`${i}1231`);
        }
      }
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT} AND RISDATE:(${risdateArray.join(" OR ")})&fields=NETINC,RISDATE&limit=20&sort_by=RISDATE&sort_order=DESC`,
        ).then((res) => {
          const data = res.data;
          const trimmedYrs: any[] = [];
          const transformedData: any[] = [];
          data.reverse().forEach((obj: any) => {
            trimmedYrs.push(obj.RISDATE.slice(0, 4));
            transformedData.push({
              yr: obj.RISDATE.slice(0, 4),
              NETINC: obj.NETINC,
            });
          });
          setData(res.data);
          setTableHeaders(getNetincHeaders(trimmedYrs));
          const row: any = {};
          transformedData.forEach(
            (obj: any) =>
              (row[obj.yr] = {
                value: formatDollar(obj.NETINC),
                styles: { fontSize: 16 },
              }),
          );
          setTableData([row]);
        });
      })();
    } else if (report === "netinc-compare") {
      let risStart = `${netincCompareYrStart}1231`;
      let risEnd = `${netincCompareYrEnd}1231`;
      if (Number(netincCompareYrEnd) === Number(risdateYr)) {
        risEnd = `${netincCompareYrEnd}${risdateQ}`;
      }
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?filters=CERT:${institution.CERT} AND RISDATE:(${risStart} OR ${risEnd})&fields=NETINC,RISDATE&limit=20&sort_by=RISDATE&sort_order=DESC`,
        ).then((res) => {
          setData(res.data.reverse());
          const transformed = [
            res.data.reduce((acc: any, item: any) => {
              const year = item.RISDATE.slice(0, 4);
              return {
                ...acc,
                [year]: {
                  value: formatDollar(item.NETINC),
                  styles: {
                    fontSize: 16,
                    textAlign: "right",
                  },
                },
              };
            }, {}),
          ];
          setTableData(transformed);
          setTableHeaders(getNetincHeaders(Object.keys(transformed[0])));
        });
      })();
    }
  }, [
    compareEndQ,
    compareEndYr,
    compareStartQ,
    compareStartYr,
    report,
    institution.CERT,
    invalidDateRange,
    risdate,
    risdateRangeEnd,
    risdateRangeStart,
    netincCompareYrStart,
    netincCompareYrEnd,
    risdateQ,
    risdateYr,
  ]);

  const validDateRangeCheck = (
    newStartYr?: string,
    newStartQ?: string,
    newEndYr?: string,
    newEndQ?: string,
  ) => {
    const start = Number(
      `${newStartYr || compareStartYr}${newStartQ || compareStartQ}`,
    );
    const end = Number(`${newEndYr || compareEndYr}${newEndQ || compareEndQ}`);
    if (start >= end) {
      setInvalidDateRange(true);
    } else if (invalidDateRange) {
      setInvalidDateRange(false);
    }
  };

  const reportIsNetinc = report === "netinc-compare";

  const customSelectProps = {
    sx: {
      "height": 30,
      "mr": 1,
      "borderRadius": 0,
      "padding": 0,
      "& .MuiSelect-select": {
        padding: "0 14px",
      },
    },
    MenuProps: {
      PaperProps: {
        sx: (theme: Theme) => ({
          "borderRadius": 0,
          "& .MuiList-root": {
            padding: 0,
          },
          "& .MuiMenuItem-root": {
            height: 30,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
  };

  const comparePeriodSelection = (
    <Box
      {...(smallScreen && {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      })}
    >
      <Box display="flex">
        <Box mr={2}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary.main"
          >
            Starting Period
          </Typography>
          {reportIsNetinc ? (
            <Select
              {...customSelectProps}
              value={netincCompareYrStart}
              error={invalidDateRange}
              onChange={(event) => {
                if (event.target.value >= netincCompareYrEnd) {
                  setInvalidDateRange(true);
                } else if (invalidDateRange) {
                  setInvalidDateRange(false);
                }
                setNetincCompareYrStart(event.target.value);
              }}
            >
              {compareOptionsYr.map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <>
              <Select
                {...customSelectProps}
                value={compareStartYr}
                error={invalidDateRange}
                onChange={(event) => {
                  if (
                    event.target.value === String(earliestRisdateYr) &&
                    Number(compareStartQ) < Number(earliestRisdateQ)
                  ) {
                    setCompareStartQ(earliestRisdateQ);
                    setCompareStartYr(event.target.value);
                  } else {
                    setCompareStartYr(event.target.value);
                  }
                  if (
                    event.target.value === risdateYr &&
                    Number(compareStartQ) > Number(risdateQ)
                  ) {
                    setCompareStartQ(risdateQ);
                    setCompareStartYr(event.target.value);
                  } else {
                    setCompareStartYr(event.target.value);
                  }
                  validDateRangeCheck(event.target.value);
                }}
              >
                {compareOptionsYr.map((yr) => (
                  <MenuItem key={yr} value={yr}>
                    {yr}
                  </MenuItem>
                ))}
              </Select>
              <Select
                {...customSelectProps}
                value={compareStartQ}
                error={invalidDateRange}
                onChange={(event) => {
                  setCompareStartQ(event.target.value);
                  validDateRangeCheck(undefined, event.target.value);
                }}
              >
                {compareOptionsStartQ.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary.main"
          >
            Ending Period
          </Typography>
          {reportIsNetinc ? (
            <Select
              {...customSelectProps}
              value={netincCompareYrEnd}
              error={invalidDateRange}
              onChange={(event) => {
                if (event.target.value <= netincCompareYrStart) {
                  setInvalidDateRange(true);
                } else if (invalidDateRange) {
                  setInvalidDateRange(false);
                }
                setNetincCompareYrEnd(event.target.value);
              }}
            >
              {compareOptionsYr.map((yr) => (
                <MenuItem key={yr} value={yr}>
                  {yr}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <>
              <Select
                {...customSelectProps}
                value={compareEndYr}
                error={invalidDateRange}
                onChange={(event) => {
                  if (
                    event.target.value === String(earliestRisdateYr) &&
                    Number(compareEndQ) < Number(earliestRisdateQ)
                  ) {
                    setCompareEndQ(earliestRisdateQ);
                    setCompareEndYr(event.target.value);
                  } else {
                    setCompareEndYr(event.target.value);
                  }
                  if (
                    event.target.value === risdateYr &&
                    Number(compareEndQ) > Number(risdateQ)
                  ) {
                    setCompareEndQ(risdateQ);
                    setCompareEndYr(event.target.value);
                  } else {
                    setCompareEndYr(event.target.value);
                  }
                  validDateRangeCheck(undefined, undefined, event.target.value);
                }}
              >
                {compareOptionsYr.map((yr) => (
                  <MenuItem key={yr} value={yr}>
                    {yr}
                  </MenuItem>
                ))}
              </Select>
              <Select
                {...customSelectProps}
                value={compareEndQ}
                error={invalidDateRange}
                onChange={(event) => {
                  setCompareEndQ(event.target.value);
                  validDateRangeCheck(
                    undefined,
                    undefined,
                    undefined,
                    event.target.value,
                  );
                }}
              >
                {compareOptionsEndQ.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </Box>
        {!smallScreen && (
          <Typography
            color="error"
            sx={{
              minHeight: "1.5em",
              visibility: invalidDateRange ? "visible" : "hidden",
            }}
          >
            End date must be after start date.
          </Typography>
        )}
      </Box>
      {smallScreen && (
        <Typography
          color="error"
          sx={{
            minHeight: "1.5em",
            visibility: invalidDateRange ? "visible" : "hidden",
          }}
        >
          End date must be after start date.
        </Typography>
      )}
    </Box>
  );

  const { categories, dataset } = transformToChartData(data, report);
  const chartConfig =
    report === "assets-qtly"
      ? {}
      : report === "assets-compare"
        ? assetCompareChartConfig
        : report === "netinc-compare"
          ? netincCompareConfig
          : report === "netinc-annual"
            ? netincChartConfig
            : report === "netint-qtly"
              ? netintQtlyChartConfig
              : netintCompareChartConfig;
  const chartType = report === "netinc-annual" ? "column2d" : "mscolumn2d";
  const dataViz = (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          width: "200%",
          transform:
            dataView === "table" ? "translateX(0)" : "translateX(-50%)",
          transition: "transform 0.6s ease",
        }}
      >
        <Box
          id="asset-table"
          sx={{
            "width": "50%",
            "height": 400,
            "tr:nth-child(even)": {
              backgroundColor: " #e9edf5",
            },
            "tr:nth-child(odd)": {
              backgroundColor: " #ffffff",
            },
          }}
        >
          <MultiHeaderTable
            tableHeaders={tableHeaders}
            tableBody={<SimpleTableBody tableData={tableData} />}
          />
        </Box>
        <Box
          id="asset-chart"
          sx={{
            "width": "50%",
            "height": 600,
            "& [class*='toolbar-master']": { display: "none" }, // hides the hamburger menu for image download
          }}
        >
          <FibFinancialsChart
            categories={categories}
            dataset={dataset}
            onRender={(chart: any) => {
              chartInstanceRef.current = chart;
            }}
            chart={chartConfig}
            chartType={chartType}
          />
        </Box>
      </Box>
    </Box>
  );

  const subtitle =
    report === "assets-compare" || report === "netint-compare"
      ? `${compareStartQ.slice(0, 2)}/${compareStartQ.slice(2)}/${compareStartYr} compared to ${compareEndQ.slice(0, 2)}/${compareEndQ.slice(2)}/${compareEndYr}`
      : report === "netinc-compare"
        ? `${netincCompareYrStart} compared to ${netincCompareYrEnd}`
        : report === "netinc-annual"
          ? `${Number(risdateYr) - 9} - ${risdateYr}`
          : `${risdateRangeStart.slice(0, 4)} - ${risdateRangeEnd.slice(0, 4)}`;

  let downloadData: string[][] = [];

  if (report.includes("asset")) {
    downloadData = [
      ["RISDATE", "ASSET"],
      ...data.map((obj) => [obj.RISDATE, String(obj.ASSET)]),
    ];
  } else if (report.includes("netinc")) {
    downloadData = [
      ["RISDATE", "NETINC"],
      ...data.map((obj) => [obj.RISDATE, String(obj.NETINC)]),
    ];
  } else if (report.includes("netint")) {
    downloadData = [
      ["RISDATE", "NIM"],
      ...data.map((obj) => [obj.RISDATE, String(obj.NIM)]),
    ];
  }

  const downloadBttn = (
    <ApiDataDownload
      apiData={[
        {
          appState: appState,
          fileName: `${fileNameFormat(title.replaceAll("- ", ""), institution.CERT, institution.NAME)}.csv`,
          apiPath: "financials",
          text: "Download Data",
          data: downloadData,
        },
        {
          appState: appState,
          fileName: "",
          apiPath: "financials",
          text: "Download Chart",
          downloadImageFn: () =>
            chartInstanceRef.current?.exportChart({
              exportFormat: "PDF",
              exportFileName: fileNameFormat(
                title.replaceAll("- ", ""),
                institution.CERT,
                institution.NAME,
              ),
            }),
        },
      ]}
      app={"bankFind"}
    />
  );

  return (
    <Box sx={{ marginTop: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex" }} {...(smallScreen && { width: "100%" })}>
          <Box {...(smallScreen && { width: "100%" })}>
            <ResultsViewToggle
              mode={dataView}
              onModeChange={(mode) => setDataView(mode)}
              options={[
                { label: "table", icon: <TableChartIcon /> },
                { label: "chart", icon: <TimelineIcon /> },
              ]}
              dropDownOptions={dropDownOptions}
              dropDown={report}
              setDropDown={(selected: string) => {
                setReport(selected as Report);
                setTitle(titles[selected as Report]);
              }}
            />
          </Box>
          {!smallScreen && (
            <Box
              ml={2}
              visibility={report.includes("compare") ? "visible" : "hidden"}
            >
              {comparePeriodSelection}
            </Box>
          )}
        </Box>
        {!smallScreen && downloadBttn}
      </Box>

      {smallScreen && (
        <Box>
          {report.includes("compare") && comparePeriodSelection}
          <Box
            mt={report.includes("compare") ? 0 : 1}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            {downloadBttn}
          </Box>
        </Box>
      )}

      <Typography variant="h2" sx={{ marginTop: 1, pb: 0 }}>
        {title}
      </Typography>

      {dataView === "chart" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" color="primary.main">
            {subtitle}
          </Typography>
          <Typography variant="subtitle1" color="primary.main">
            Values are in Thousands (USD)
          </Typography>
        </Box>
      ) : (
        <Typography variant="subtitle1" color="primary.main" mb={1}>
          {subtitle}
        </Typography>
      )}
      {dataViz}
    </Box>
  );
};
